import { createContext, useContext, useState, ReactNode, useEffect } from "react";

type Language = "en" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
  translations: typeof translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    nav: {
      home: "HOME",
      about: "AI GALLERY",
      work: "SERVICES",
      services: "SERVICES",
      contact: "CONTACT",
      quickpick: "Quick Pick",
      videoQuickPicks: "Video Quick Picks",
      photoQuickPicks: "Photo Quick Picks",
      cart: "Cart",
      generate: "AI GENERATOR",
      desk: "ART DIRECTION"
    },
    home: {
      hero: {
        titleLine1: "Design, motion, and story —",
        titleLine2: "filmed, generated, or combined.",
        subtitle: "Production, AI-generated, or blended narratives.",
        cta1: "Quick Pick",
        cta2: "View our work",
        ctaServices: "Services",
        ctaProcess: "Start → Finish",
        arrowServices: "shop all services",
        arrowPackages: "shop packages"
      },
      quickpick: {
        title: "Quick Pick: Fast. Simple. Studio-Quality.",
        subtitle: "Cinematic Work, Without the Wait.\nSelect Video, Photo, or both — special yearly pricing applied.",
        cta: "Quick Pick quote",
        whatIsTitle: "What Quick Pick Is",
        fastestWay: "The fastest way to get curated, high-quality AI visuals",
        builtFromIdea: "Built from your idea, delivered with our studio's design standards.",
        youTellUs: "You tell us what you want. We generate it.",
        simpleFast: "Simple, fast, and consistent. No long calls, no complex briefs, no waiting.",
        perfect: "Perfect for anyone who needs strong creative output, quickly.",
        // Cinematic directions for homepage
        directions: {
          sectionTitle: "Choose Your Direction",
          social: {
            title: "Social",
            tagline: "Immediate image + short video package with impact."
          },
          cinematic: {
            title: "Creator",
            tagline: "Full brand and storytelling."
          },
          suite: {
            title: "Full Suite",
            tagline: "World-building, total coverage."
          },
          enter: "Enter"
        },
        videoGeneration: {
          title: "VIDEO GENERATION",
          description: "AI-generated video from 15 to 60 seconds. Fast turnaround. Perfect for social content, ads, & brand stories."
        },
        photoGeneration: {
          title: "PHOTO GENERATION",
          description: "AI-generated images from single shots to full campaign packs. Fast turnaround. Perfect for social content, ads, & brand stories."
        },
        viewPackages: "View packages",
        scroll: "Scroll to explore",
        sectionLabel: "Quick Pick",
        sectionTitle: "Choose your direction",
        sectionIntro: "Three directions. One studio. Your vision, realized.",
        hoverToPreview: "Hover to preview",
        videoTab: "Video",
        photoTab: "Photo",
        comboTab: "Video + Photo",
        addToQuote: "Add to quote",
        packageAdded: "added to quote",
        removed: "removed from quote",
        tapToView: "Tap again to view",
        mostPopular: "MOST POPULAR",
        features: {
          cinematicProduction: "Cinematic production",
          storyDriven: "Story-driven approach",
          fullHD: "Full-HD delivery",
          highQuality: "High-quality images",
          consistentStyle: "Consistent style",
          studioGrade: "Studio-grade lighting",
          versatile: "Versatile compositions",
          narrative: "Narrative-friendly sequence",
          fullCampaign: "Full campaign coverage",
          cohesiveStory: "Cohesive visual story",
          perfectSync: "Perfect design sync"
        },
        videoPackages: {
          p15: {
            title: "15-Second Quick Pick",
            description: "Perfect for hooks, teasers, and quick attention-grabbers.\nA tight, scroll-stopping burst of cinematic energy."
          },
          p30: {
            title: "30-Second Quick Pick",
            description: "Perfect for product highlights, simple explainers, and paid ads.\nMore time, more impact — still fast, sharp, and studio-clean."
          },
          p45: {
            title: "45-Second Quick Pick",
            description: "Perfect for deeper explainers and service overviews.\nRoom to breathe and tell a story — quick and budget-friendly."
          },
          p60: {
            title: "60-Second Quick Pick",
            description: "Perfect for brand intros and full product walkthroughs.\nA complete story arc with beautiful pacing, crafted for campaigns or launches."
          }
        },
        photoPackages: {
          p1: {
            title: "Single Image — Quick Snap",
            description: "A clean, styled hero image — fast.\nPerfect for a quick highlight, cover image, or profile moment."
          },
          p4: {
            title: "4-Image Set — Essentials Pack",
            description: "Your brand, four different ways.\nA tight, curated batch of visuals ready for social, ads, or product drops."
          },
          p6: {
            title: "6-Image Set — Social Stretch",
            description: "A small visual story told across six frames.\nPerfect for weekly socials, mini-campaigns, or mood-board-ready content."
          },
          p10: {
            title: "10-Image Set — Campaign Kit",
            description: "A full image rollout in one fast delivery.\nBrand-intro level quality — versatile enough for ads, site banners, or launches."
          }
        },
        comboPackages: {
          combo1: {
            name: "15s + 1 Image",
            subtitle: "Flash Hero Combo",
            description: "Perfect for hooks, teasers, and one strong hero visual.\nA punchy motion piece paired with one high-impact final image."
          },
          combo2: {
            name: "30s + 4 Images",
            subtitle: "Mini Set Combo",
            description: "Perfect for product highlights and short campaigns.\nA clean motion showcase with a small set of polished, matching images."
          },
          combo3: {
            name: "45s + 6 Images",
            subtitle: "Social Pack Combo",
            description: "Perfect for a week of posts or a simple launch.\nEnough content to fill a full posting cycle with one unified look."
          },
          combo4: {
            name: "60s + 10 Images",
            subtitle: "Campaign Pack Combo",
            description: "Perfect for brand intros and full product walkthroughs.\nA complete motion + stills kit designed for full-scale campaigns."
          }
        }
      },
      cart: {
        selectedPackage: "Selected package",
        continueText: "Continue to contact page to complete your inquiry.",
        clear: "Clear",
        continue: "Continue"
      },
      work: {
        hero: {
          title: "Easy Peasy.",
          subtitle: "Our process, start to finish + services"
        },
        steps: {
          step1: {
            title: "You Share the Idea",
            content: [
              "Could be tight.",
              "Could be messy.",
              "Could be a sentence.",
              "",
              "We're used to building full visuals from small starting points — that's the filmmaking brain.",
              "",
              "You tell us the vibe, the style, the purpose, or whatever you've got."
            ]
          },
          step2: {
            title: "We Build the Direction",
            content: [
              "We take your idea and give it shape:",
              "• look + mood",
              "• pacing",
              "• style references",
              "• color and tone",
              "• the overall feel",
              "",
              "Exactly like planning a shot list or creative board — only lighter and faster."
            ]
          },
          step3: {
            title: "We Generate Using AI (Filmmaker Style)",
            content: [
              "AI is basically our crazy new toolbox.",
              "",
              "It lets us:",
              "• create scenes we could never afford",
              "• build worlds that don't exist",
              "• push ideas farther",
              "• experiment without waiting on gear or locations",
              "• try \"what ifs\" instantly",
              "",
              "But the decisions behind it — framing, light, style, consistency —",
              "that's all still rooted in the filmmaking we started with."
            ]
          },
          step4: {
            title: "We Refine Everything",
            content: [
              "Raw output is just the starting point.",
              "",
              "We clean it, adjust it, and lock the look:",
              "• fix details",
              "• match style",
              "• correct edges + faces",
              "• refine lighting",
              "• polish the frame",
              "",
              "This is the part that makes it feel intentional, not random."
            ]
          },
          step5: {
            title: "You Review",
            content: [
              "We send you the first round.",
              "You tell us what you're feeling, what you like, what you'd change.",
              "",
              "Simple notes, quick responses — filmmaker-style collaboration."
            ]
          },
          step6: {
            title: "Final Delivery",
            content: [
              "We package everything clean:",
              "• final files",
              "• ready-to-use formats",
              "• consistent look across all assets",
              "",
              "Done."
            ]
          }
        },
        services: {
          sectionLabel: "ALL SERVICES",
          sectionTitle: "Our Mix & Match Menu",
          selectTitle: "Select your services",
          continueButton: "Continue",
          service1: {
            title: "AI Photo Generation",
            description: "Custom images built from your ideas — clean, consistent, and in the exact style you want. Great for campaigns, concepts, products, characters, scenes… anything visual."
          },
          service2: {
            title: "AI Video Generation",
            description: "Short-form videos generated from style, storyboard, or description. Looks polished, feels intentional, and moves like something directed — not random."
          },
          service3: {
            title: "Brand & Logo Design",
            description: "Modern, minimal brand identities shaped through a mix of classic design principles and AI exploration. We build logos, type direction, color systems, and full brand kits."
          },
          service4: {
            title: "Website Design",
            description: "Clean, sharp, future-ready sites. We design everything — hero layouts, structure, copy tone, UI elements — and help you launch fast."
          },
          service5: {
            title: "Creative Direction",
            description: "Whether it's AI or traditional production, we guide the look, mood, rhythm, and overall story. We make sure your visuals feel cohesive instead of \"generated.\""
          },
          service6: {
            title: "Production Tools & Systems",
            description: "We build custom workflows that keep your AI content consistent — style, characters, prompts, and brand outputs — so everything looks unified across every platform."
          },
          service7: {
            title: "Chat Agents (Custom AI Teams)",
            description: "We build and train your own AI agents — a team built around your brand, voice, tasks, and goals. From simple assistants to full creative pipelines, we structure them like a real studio."
          },
          service8: {
            title: "Traditional Filmmaking & Editing",
            description: "Still part of who we are. We shoot, light, edit, color, cut, and produce traditionally when needed. This is the foundation that keeps the AI work grounded and intentional."
          },
          service9: {
            title: "Design & Visual Consulting",
            description: "If you already use AI, we help you clean it up: better prompts, better style control, stable characters, consistent outputs, clearer creative decisions."
          },
          service10: {
            title: "Custom Creative Projects",
            description: "Anything that blends filmmaking, design, and AI — from wild concepts to polished brand visuals. If it's visual or story-driven, we can help build it."
          },
          customizeButton: "Customize With Us"
        },
        cta: {
          title: "Let's Make Something",
          description: "If you've got an idea, let's see what it can turn into — whether it's grounded, cinematic, bizarre, or something in between.",
          button: "Start a Project"
        }
      },
      clarity: {
        title: "Clear, useful intelligence.",
        body: "We connect strategy, interfaces and content so your AI feels simple, not overwhelming.",
        pillars: {
          strategy: "Strategy that starts from your real problems.",
          interface: "Interfaces that people actually enjoy using.",
          intelligence: "Intelligence that explains itself and earns trust."
        }
      },
      worlds: {
        title: "Cinematic worlds for your brand.",
        body: "We design visual stories that feel like a film, built with AI."
      },
      systems: {
        title: "Systems that scale with you.",
        body: "From prompts to full workflows, we create repeatable systems that your team can use every day."
      },
      lab: {
        title: "The Alabama Lab.",
        body: "A safe space to prototype new visuals, stories and tools before you commit."
      },
      flow: {
        title: "How it works",
        step1Title: "Share your idea",
        step1Body: "Send us your notes, references, or brand info. Simple is fine.",
        step2Title: "We build the first version",
        step2Body: "We generate your visuals using our internal tools and creative direction.",
        step3Title: "You request small tweaks",
        step3Body: "One light revision is included in every Quick Pick.",
        step4Title: "Final delivery",
        step4Body: "You receive your finished assets, ready to use anywhere."
      },
      testimonials: {
        title: "What our clients say.",
        empty: "Testimonials coming soon."
      },
      cta: {
        title: "Ready when you are.",
        desc: "Tell us what you're trying to build and we'll help you design the AI around it.",
        button: "Talk to the studio"
      }
    },
    services: {
      hero: {
        title: "What we build.",
        subtitle: "AI systems, interfaces, and narratives crafted with precision."
      },
      ai: {
        title: "AI Consulting",
        subtitle: "Intelligent systems that work with your team"
      },
      content: {
        title: "Content Production",
        subtitle: "Stories that connect and convert"
      },
      items: {
        ai: {
          title: "AI Systems",
          body: "Intelligent pipelines, agents, and automations that support your teams."
        },
        interfaces: {
          title: "Interface Design",
          body: "Calm, cinematic, glass-like interfaces that stay legible and human."
        },
        content: {
          title: "Content & Story",
          body: "Brand voice, narratives, and generative editorial flows that stay on message."
        },
        engineering: {
          title: "Creative Engineering",
          body: "Prototypes, experiments, and realtime systems that push your ideas further."
        },
        automation: {
          title: "Process Automation",
          description: "Optimize operations and free up your team's time.",
          features: [
            "Current workflow analysis",
            "Custom automation solution design",
            "Implementation and integration with existing systems"
          ]
        },
        chatbots: {
          title: "Chatbots & AI Agents",
          description: "Serve your customers 24/7 with intelligent agents.",
          features: [
            "Advanced conversational chatbots",
            "Integration with WhatsApp, web and social media",
            "Continuous learning and automatic improvement"
          ]
        },
        dataAnalysis: {
          title: "Data Analysis",
          description: "Transform data into actionable insights.",
          features: [
            "Interactive dashboards and automated reports",
            "Predictive analysis and forecasting",
            "AI-powered recommendations"
          ]
        },
        aiImplementation: {
          title: "AI Implementation",
          description: "Integrate AI models into your products.",
          features: [
            "Selection of appropriate models and architectures",
            "API development and integration",
            "Continuous maintenance and optimization"
          ]
        },
        strategicConsulting: {
          title: "Strategic Consulting",
          description: "Define your digital transformation roadmap.",
          features: [
            "AI opportunity audit",
            "Implementation strategy design",
            "Training and change management"
          ]
        },
        scriptsCopy: {
          title: "Scripts & Copy",
          description: "Text that converts and resonates with your audience.",
          features: [
            "Scripts for videos and podcasts",
            "Copy for landing pages and ads",
            "SEO-optimized content"
          ]
        },
        socialContent: {
          title: "Social Media Content",
          description: "Keep your digital presence always active.",
          features: [
            "Strategic content calendars",
            "Posts, stories and multimedia content",
            "Community management and engagement"
          ]
        },
        videoProduction: {
          title: "Video Production",
          description: "Videos that capture attention and drive action.",
          features: [
            "Corporate and explainer videos",
            "Social media content",
            "Professional editing and post-production"
          ]
        },
        newsletters: {
          title: "Newsletters",
          description: "Keep your audience informed and engaged.",
          features: [
            "Email marketing strategy design",
            "Persuasive content writing",
            "Metrics analysis and optimization"
          ]
        },
        storytelling: {
          title: "Strategic Storytelling",
          description: "Build narratives that define your brand.",
          features: [
            "Brand story development",
            "Messaging and positioning",
            "Long-form content and thought leadership"
          ]
        },
        aiAudiovisual: {
          title: "AI Audiovisual Production",
          description: "Next-generation videos powered by artificial intelligence.",
          features: [
            "AI video generation",
            "Automated editing and effects",
            "Digital avatars and AI voiceovers"
          ]
        },
        upcoming: {
          title: "Coming Soon",
          description: "Innovations and services in development.",
          features: [
            "Custom AI agents",
            "Complete automation platforms",
            "Vertical AI solutions by industry"
          ]
        }
      },
      cta: {
        title: "Let's build something together",
        desc: "Tell us your needs and we'll design a custom solution.",
        button1: "Get started",
        button2: "View our work"
      },
      filter: {
        all: "All",
        ia: "AI",
        content: "Content"
      },
      quickpick: {
        title: "Quick Pick Studio",
        subtitle: "Fast, playful AI content — pick a package, send a brief, and get ready-to-post video or images.",
        cta: "Get a Quick Pick quote",
        video: {
          p15: {
            title: "15-second Quick Pick",
            description: "Perfect for hooks, teasers, stories, short ads.",
            price: "$600 MXN"
          },
          p30: {
            title: "30-second Quick Pick",
            description: "Perfect for product highlights, simple explainers, paid ads.",
            price: "$900 MXN"
          },
          p45: {
            title: "45-second Quick Pick",
            description: "Perfect for slightly deeper explainers and service overviews.",
            price: "$1,200 MXN"
          },
          p60: {
            title: "60-second Quick Pick",
            description: "Perfect for brand intros and full product walkthroughs.",
            price: "$1,500 MXN"
          }
        },
        image: {
          p1: {
            title: "1 Image — Flash Hero",
            description: "Perfect for one key visual, hero banner, or cover image.",
            price: "From $50 MXN"
          },
          p4: {
            title: "4 Images — Mini Set",
            description: "Perfect for short campaigns, carousels, and small product sets.",
            price: "From $100 MXN"
          },
          p6: {
            title: "6 Images — Social Pack",
            description: "Perfect for a week of posts or simple launches.",
            price: "From $180 MXN"
          },
          p10: {
            title: "10 Images — Campaign Pack",
            description: "Perfect for product launches, multi-channel campaigns, and content libraries.",
            price: "From $250 MXN"
          }
        },
        form: {
          name: "Name",
          email: "Email",
          phone: "WhatsApp / phone",
          type: "What do you need?",
          typeVideo: "Video package",
          typeImage: "Image package",
          typeBoth: "Both video and images",
          message: "Describe your idea, brand, and any links or references",
          submit: "Send Quick Pick brief",
          success: "Thank you. We'll contact you by email and text with a quote.",
          error: "Something went wrong. Please try again."
        },
        cart: {
          addToQuote: "Add to quote",
          removeFromQuote: "Remove",
          viewQuote: "View quote",
          itemsLabel: "Selected packages",
          totalLabel: "Estimated total",
          continueBtn: "Continue to brief",
          empty: "No packages selected yet",
          title: "Your Quote",
          removeItem: "Remove",
          note: "+ IVA. This is a quote inquiry, not a purchase.",
          nameLabel: "Name (optional)",
          emailLabel: "Email",
          checkoutCta: "Request Quote",
          confirmTitle: "Quote request sent!",
          confirmBody: "We'll contact you soon via email with your custom quote.",
          errorTitle: "Error",
          errorBody: "Something went wrong. Please try again."
        }
      }
    },
    work: {
      hero: {
        title: "Selected work.",
        subtitle: "Systems, stories, and interfaces built for ambitious teams."
      },
      intro: {
        title: "A studio shaped by experimentation.",
        body: "Every project is a collaboration, a discovery, a new world."
      },
      cases: {
        spectrum_os: {
          title: "Spectrum OS — AI Interface System",
          summary: "A calm operating environment for managing multiple AI agents, designed as a cinematic glass UI.",
          tags: ["AI interfaces", "Multi-agent", "Product design"]
        },
        lucent: {
          title: "Lucent — Intelligent Content Engine",
          summary: "A generative content system that keeps brand voice consistent across campaigns and channels.",
          tags: ["Content automation", "Brand voice", "LLM"]
        },
        zero_point: {
          title: "ZeroPoint — Agent Orchestration Suite",
          summary: "An orchestration layer that coordinates tools, prompts, and agents into a single controllable stack.",
          tags: ["Agents", "Orchestration", "Systems"]
        }
      },
      experimental: {
        title: "Experimental AI Studies",
        body: "Concepts, prototypes, and visual experiments exploring motion, mood, and AI-driven imagery."
      }
    },
    about: {
      hero: {
        title: "Built on real production. Expanded with AI.",
        subtitle: "Built on traditional craft. Enhanced with AI tools.",
        scrollCta: "Our growing work"
      },
      story: {
        title: "Who we are.",
        p1: "We come from product, film and branding.",
        p2: "We speak business, design and technology at the same time.",
        p3: "Our work is calm, intentional and made to last."
      },
      media: {
        portrait: {
          title: "A studio, not a factory.",
          desc: "Every project is treated as a collaboration, not a ticket."
        }
      },
      approach: {
        title: "Our approach",
        ai: {
          title: "AI strategy",
          desc: "We help you understand what AI can actually do for your business, not just what's trendy."
        },
        creative: {
          title: "Creative thinking",
          desc: "Every solution is tailored to your brand's voice, audience, and goals."
        }
      },
      team: {
        title: "The team"
      },
      values: {
        title: "Our values"
      },
      cta: {
        title: "Ready to work together?",
        desc: "Let's talk about your project.",
        button: "Get in touch"
      }
    },
    contact: {
      hero: {
        title: "Tell us what you're building.",
        subtitle: "A short message is enough. We'll reply with ideas, questions and a simple next step."
      },
      form: {
        name: "Name",
        email: "Email",
        company: "Company",
        service: "What are you interested in?",
        budget: "Budget range",
        message: "What do you want to create?",
        submit: "Send message",
        success: "Thank you. We'll be in touch soon.",
        error: "Something went wrong. Please try again.",
        required: "*",
        placeholder: {
          name: "Your name",
          email: "your@email.com",
          company: "Your company",
          service: "Select a service",
          budget: "Select a range",
          message: "Tell us about your project..."
        }
      },
      info: {
        title: "Get in touch",
        email: "Email",
        whatsapp: "WhatsApp",
        location: "Location",
        chat: "Chat on WhatsApp",
        "location.address": "Alabama 29, CDMX"
      },
      expect: {
        title: "What to expect",
        "1": "We'll reply within 24 hours",
        "2": "A short call to understand your needs",
        "3": "A clear proposal with timeline and pricing",
        "4": "No pressure, no sales pitch"
      },
      faq: {
        title: "Common questions",
        subtitle: "Quick answers about working with us",
        more: "Have more questions?",
        button: "Ask on WhatsApp",
        q1: {
          question: "How long does a typical project take?",
          answer: "It depends on scope, but most of our AI consulting projects are completed in 4-8 weeks, while content projects can range from 2-6 weeks. We work with agile sprints to deliver incremental value."
        },
        q2: {
          question: "Do you work with companies of all sizes?",
          answer: "Yes, we work with both early-stage startups and established companies. We adapt our services and proposals to the specific size and needs of each client."
        },
        q3: {
          question: "Do you offer post-implementation support?",
          answer: "Absolutely. All our projects include an initial support period, and we offer ongoing maintenance plans to ensure your AI and content solutions continue to function optimally."
        },
        q4: {
          question: "Do I need technical knowledge to work with you?",
          answer: "Not at all. We handle all the technical aspects and explain everything in business terms. Our goal is for you to understand the value and results, not the technicalities."
        },
        q5: {
          question: "How do you measure project success?",
          answer: "We define clear KPIs from the start: ROI, time saved, increased conversion, engagement, etc. Each project includes reports with concrete metrics to measure real impact on your business."
        },
        q6: {
          question: "Can I hire just AI consulting or just content?",
          answer: "Of course. While many clients leverage both services for a comprehensive strategy, you can hire only what you need. We'll help you identify what's most important for your situation."
        },
        q7: {
          question: "Do you work with international clients?",
          answer: "Yes, we work with clients throughout Latin America, the United States, and Europe. Our team is accustomed to collaborating remotely and across different time zones."
        },
        q8: {
          question: "What makes Alabama Studio different from other agencies?",
          answer: "We're unique in combining deep technical AI expertise with high-level creativity. We're not just developers or just creatives: we deeply understand both worlds and how to make them work together to generate real results."
        },
        q9: {
          question: "How do we start working together?",
          answer: "It's very simple: schedule a free initial call where we'll learn about your business and challenges. Then we'll send you a personalized proposal. If it works for you, let's go! All communication is clear and straightforward."
        }
      },
      services: {
        selectedLabel: "Selected Services",
        serviceOptions: {
          ia: "AI Consulting",
          content: "Content Production",
          both: "Both services",
          other: "Other"
        }
      }
    },
    app: {
      loading: {
        label: "Loading the experience…",
        alt: "ALABAMA Ai loading illustration"
      }
    },
    footer: {
      companyName: "Alabama Studio",
      tagline: "Make. Play. Repeat.",
      quicklinks: "Quick Links",
      services: "Services",
      rights: "All rights reserved.",
      ai: "AI Consulting",
      content: "Content Studio",
      hello: "Hello"
    },
    quickpick: {
      hero: {
        title: "Choose a direction",
        subtitle: "An entry point — not a decision."
      },
      arrival: {
        title: "Choose a direction",
        subtitle: "An entry point, not a decision.",
        enter: "Enter"
      },
      directions: {
        social: {
          title: "Social Direction",
          philosophy: "Immediate, aesthetic-driven image creation.\nBuilt for presence, pace, and culture — fast visuals that feel current without feeling disposable."
        },
        cinematic: {
          title: "Cinematic Creator",
          philosophy: "Story, motion, and intention-led video.\nLighting, pacing, and narrative come first — designed to feel considered, not automated."
        },
        suite: {
          title: "Full Cinematic Suite",
          philosophy: "World-building through image and motion.\nA complete visual system where scale, tone, and continuity work together."
        }
      },
      packages: {
        social: {
          title: "Social Direction",
          oneliner: "A fast visual direction pass that makes your brand look intentional.",
          included: [
            "A tight set of styled images (social + ads ready)",
            "One clear look: color, contrast, texture",
            "Crops for the formats you actually use",
            "Lightweight polish, delivered fast"
          ],
          ctaHelper: "Best when you need momentum, not a big production."
        },
        cinematic: {
          title: "Cinematic Creator",
          oneliner: "Short-form video, directed like film—clean, paced, and precise.",
          included: [
            "A hero clip built from a few strong shots",
            "Edit pacing + titles that feel editorial",
            "Sound polish (no cheesy effects)",
            "Cutdowns for socials if needed"
          ],
          ctaHelper: "Built for brands that want \"cinematic\" without the bloat."
        },
        suite: {
          title: "Full Cinematic Suite",
          oneliner: "The full package—story, direction, and a complete set of assets.",
          included: [
            "Concept + structure (we shape the story first)",
            "A premium \"director suite\" pass: controlled camera, lens, lighting, blocking",
            "The hero film + supporting cutdowns",
            "Stills pulled or designed to match the film look",
            "Priority polish + tighter consistency across everything"
          ],
          ctaHelper: "If you want the highest-end result, start here."
        }
      },
      framing: {
        short: "Short form — a focused moment, tight and intentional",
        extended: "Extended — space to breathe and develop",
        long: "Long form — full narrative arc"
      },
      cta: {
        enter: "Enter"
      }
    },
    generate: {
      banner: {
        title: "DECEMBER SPECIAL:",
        subtitle: "FREE Image Generation + Early Access to Alabama Ai.",
        expires: "Offer Ends Dec 31.",
        placeholder: "Enter email",
        button: "Get Access",
        success: "You're in!"
      },
      empty: {
        title: "Your generated images will appear here",
        subtitle: "Start by describing what you want to create"
      },
      processing: "Processing",
      placeholder: "Create anything...",
      button: "Generate",
      tooltip: "Please enter a prompt to generate.",
      dragHint: "Drag & drop images to edit",
      edit: "Edit",
      download: "Download",
      downloadSuccess: "Image downloaded!",
      shareSuccess: "Image shared!",
      downloadCancelled: "Download cancelled",
      copySuccess: "Image URL copied!",
      editHint: "Image added for editing. Add your edit prompt.",
      unlimited: "Unlimited",
      toastCreated: "Image created",
      toastCreatedMultiple: "images created",
      toastListSuccess: "You're on the list! We'll notify you when early access is ready.",
      toastError: "Something went wrong. Please try again.",
      invalidFile: "Invalid file",
      invalidFileDesc: "Please upload an image file.",
      generationFailed: "Generation failed",
      generationFailedDesc: "Please try again.",
      addPrompt: "Add a prompt to generate",
      maxProcessing: "Maximum 3 images processing at once. Please wait.",
      promptDirector: {
        label: "Prompt Director",
        subtitle: "Direction, refinement, and continuation.",
        minimize: "Minimize",
        close: "Close",
        restore: "Open Prompt Director",
        grid: "Grid",
        size: "Size",
        yourRenders: "Your renders",
        hint: "Talk to the creative director."
      },
      mai: {
        director: "MAI — Director",
        minimize: "Minimize",
        close: "Close",
        backToGallery: "Back to gallery",
        grid: "Grid",
        size: "Size",
        yourRenders: "Your renders",
        continue: "Continue"
      },
      // Active selection states
      active: "Active",
      setActive: "Set Active",
      selectedForDirector: "Selected for Prompt Director",
      deselected: "Deselected"
    }
  },
  es: {
    nav: {
      home: "INICIO",
      about: "GALERÍA IA",
      work: "SERVICIOS",
      services: "SERVICIOS",
      contact: "CONTACTO",
      quickpick: "Quick Pick",
      videoQuickPicks: "Quick Pick Video",
      photoQuickPicks: "Quick Pick Photo",
      cart: "Carrito",
      generate: "GENERADOR DE IA",
      desk: "DIRECCIÓN DE ARTE"
    },
    home: {
      hero: {
        titleLine1: "Diseño, movimiento e historia —",
        titleLine2: "filmado, generado o combinado.",
        subtitle: "Producción, generado con IA o narrativas híbridas.",
        cta1: "Quick Pick",
        cta2: "Ver nuestro trabajo",
        ctaServices: "Servicios",
        ctaProcess: "Start → Finish",
        arrowServices: "compra todos los servicios",
        arrowPackages: "compra paquetes"
      },
      quickpick: {
        title: "Quick Pick: Rápido. Simple. Calidad de Estudio.",
        subtitle: "Trabajo Cinematográfico, Sin la Espera.\nSelecciona Video, Foto o ambos — precios promocionales anuales aplicados.",
        cta: "Cotización Quick Pick",
        whatIsTitle: "Qué es Quick Pick",
        fastestWay: "La forma más rápida de obtener visuales de IA curados y de alta calidad",
        builtFromIdea: "Construido desde tu idea, entregado con los estándares de diseño de nuestro estudio.",
        youTellUs: "Tú nos dices qué quieres. Nosotros lo generamos.",
        simpleFast: "Simple, rápido y consistente. Sin llamadas largas, sin briefs complejos, sin esperas.",
        perfect: "Perfecto para cualquiera que necesite producción creativa fuerte, rápidamente.",
        // Cinematic directions for homepage
        directions: {
          sectionTitle: "Elige Tu Dirección",
          social: {
            title: "Social",
            tagline: "Paquete de imágenes y video corto con impacto inmediato."
          },
          cinematic: {
            title: "Creador",
            tagline: "Branding completo y storytelling."
          },
          suite: {
            title: "Suite Completa",
            tagline: "Construcción de universo visual, cobertura total."
          },
          enter: "Entrar"
        },
        videoGeneration: {
          title: "GENERACIÓN DE VIDEO",
          description: "Video generado con IA de 15 a 60 segundos. Entrega rápida. Perfecto para contenido social, anuncios e historias de marca."
        },
        photoGeneration: {
          title: "GENERACIÓN DE FOTO",
          description: "Imágenes generadas con IA desde tomas individuales hasta packs completos de campaña. Entrega rápida. Perfecto para contenido social, anuncios e historias de marca."
        },
        viewPackages: "Ver paquetes",
        scroll: "Desliza para explorar",
        sectionLabel: "Quick Pick",
        sectionTitle: "Elige tu dirección",
        sectionIntro: "Tres direcciones. Un estudio. Tu visión, realizada.",
        hoverToPreview: "Pasa el cursor para previsualizar",
        videoTab: "Video",
        photoTab: "Foto",
        comboTab: "Video + Foto",
        addToQuote: "Agregar a cotización",
        packageAdded: "agregado a cotización",
        removed: "eliminado de cotización",
        tapToView: "Toca de nuevo para ver",
        mostPopular: "MÁS POPULAR",
        features: {
          cinematicProduction: "Producción cinematográfica",
          storyDriven: "Enfoque narrativo",
          fullHD: "Entrega Full-HD",
          highQuality: "Imágenes de alta calidad",
          consistentStyle: "Estilo consistente",
          studioGrade: "Iluminación de estudio",
          versatile: "Composiciones versátiles",
          narrative: "Secuencia narrativa",
          fullCampaign: "Cobertura de campaña completa",
          cohesiveStory: "Historia visual cohesiva",
          perfectSync: "Sincronización perfecta"
        },
        videoPackages: {
          p15: {
            title: "Quick Pick de 15 Segundos",
            description: "Perfecto para hooks, teasers y captadores de atención rápidos.\nUna explosión cinematográfica compacta que detiene el scroll."
          },
          p30: {
            title: "Quick Pick de 30 Segundos",
            description: "Perfecto para destacar productos, explicaciones simples y anuncios pagados.\nMás tiempo, más impacto — aún rápido, nítido y con acabado de estudio."
          },
          p45: {
            title: "Quick Pick de 45 Segundos",
            description: "Perfecto para explicaciones más profundas y presentaciones de servicios.\nEspacio para respirar y contar una historia — rápido y accesible."
          },
          p60: {
            title: "Quick Pick de 60 Segundos",
            description: "Perfecto para intros de marca y recorridos completos de producto.\nUn arco narrativo completo con ritmo hermoso, diseñado para campañas o lanzamientos."
          }
        },
        photoPackages: {
          p1: {
            title: "Imagen Individual — Quick Snap",
            description: "Una imagen hero limpia y estilizada — rápida.\nPerfecta para un destacado rápido, imagen de portada o momento de perfil."
          },
          p4: {
            title: "Set de 4 Imágenes — Pack Esencial",
            description: "Tu marca, cuatro formas diferentes.\nUn lote compacto y curado de visuales listos para redes, anuncios o lanzamientos de productos."
          },
          p6: {
            title: "Set de 6 Imágenes — Social Stretch",
            description: "Una pequeña historia visual contada en seis cuadros.\nPerfecto para redes semanales, mini-campañas o contenido listo para mood board."
          },
          p10: {
            title: "Set de 10 Imágenes — Kit de Campaña",
            description: "Un despliegue completo de imágenes en una entrega rápida.\nCalidad de intro de marca — lo suficientemente versátil para anuncios, banners de sitio o lanzamientos."
          }
        },
        comboPackages: {
          combo1: {
            name: "15s + 1 Imagen",
            subtitle: "Combo Flash Hero",
            description: "Perfecto para hooks, teasers y un visual hero fuerte.\nUna pieza de movimiento contundente emparejada con una imagen final de alto impacto."
          },
          combo2: {
            name: "30s + 4 Imágenes",
            subtitle: "Combo Mini Set",
            description: "Perfecto para destacar productos y campañas cortas.\nUn showcase de movimiento limpio con un pequeño set de imágenes pulidas y coordinadas."
          },
          combo3: {
            name: "45s + 6 Imágenes",
            subtitle: "Combo Pack Social",
            description: "Perfecto para una semana de posts o un lanzamiento simple.\nSuficiente contenido para llenar un ciclo completo de publicación con un look unificado."
          },
          combo4: {
            name: "60s + 10 Imágenes",
            subtitle: "Combo Pack de Campaña",
            description: "Perfecto para intros de marca y recorridos completos de producto.\nUn kit completo de movimiento + stills diseñado para campañas de escala completa."
          }
        }
      },
      cart: {
        selectedPackage: "Paquete seleccionado",
        continueText: "Continúa a la página de contacto para completar tu solicitud.",
        clear: "Limpiar",
        continue: "Continuar"
      },
      work: {
        hero: {
          title: "Facilísimo.",
          subtitle: "Nuestro proceso, de inicio a fin + servicios"
        },
        steps: {
          step1: {
            title: "Compartes la Idea",
            content: [
              "Puede ser precisa.",
              "Puede ser desordenada.",
              "Puede ser una oración.",
              "",
              "Estamos acostumbrados a construir visuales completos desde pequeños puntos de partida — es el cerebro de cineasta.",
              "",
              "Nos dices el ambiente, el estilo, el propósito, o lo que tengas."
            ]
          },
          step2: {
            title: "Construimos la Dirección",
            content: [
              "Tomamos tu idea y le damos forma:",
              "• look + ambiente",
              "• ritmo",
              "• referencias de estilo",
              "• color y tono",
              "• la sensación general",
              "",
              "Exactamente como planear una lista de tomas o tablero creativo — solo que más ligero y rápido."
            ]
          },
          step3: {
            title: "Generamos Usando IA (Estilo Cineasta)",
            content: [
              "La IA es básicamente nuestra nueva caja de herramientas loca.",
              "",
              "Nos permite:",
              "• crear escenas que nunca podríamos pagar",
              "• construir mundos que no existen",
              "• empujar ideas más lejos",
              "• experimentar sin esperar equipo o locaciones",
              "• probar \"qué pasaría si\" instantáneamente",
              "",
              "Pero las decisiones detrás de eso — encuadre, luz, estilo, consistencia —",
              "todo eso sigue arraigado en el cine con el que empezamos."
            ]
          },
          step4: {
            title: "Refinamos Todo",
            content: [
              "El resultado crudo es solo el punto de partida.",
              "",
              "Lo limpiamos, ajustamos y aseguramos el look:",
              "• arreglamos detalles",
              "• igualamos estilo",
              "• corregimos bordes + rostros",
              "• refinamos iluminación",
              "• pulimos el cuadro",
              "",
              "Esta es la parte que hace que se sienta intencional, no aleatorio."
            ]
          },
          step5: {
            title: "Tú Revisas",
            content: [
              "Te enviamos la primera ronda.",
              "Nos dices qué sientes, qué te gusta, qué cambiarías.",
              "",
              "Notas simples, respuestas rápidas — colaboración estilo cineasta."
            ]
          },
          step6: {
            title: "Entrega Final",
            content: [
              "Empaquetamos todo limpio:",
              "• archivos finales",
              "• formatos listos para usar",
              "• look consistente en todos los assets",
              "",
              "Listo."
            ]
          }
        },
        services: {
          sectionLabel: "TODOS LOS SERVICIOS",
          sectionTitle: "Nuestro Menú Mix & Match",
          selectTitle: "Selecciona tus servicios",
          continueButton: "Continuar",
          service1: {
            title: "Generación de Fotos IA",
            description: "Imágenes personalizadas construidas desde tus ideas — limpias, consistentes y en el estilo exacto que quieres. Geniales para campañas, conceptos, productos, personajes, escenas… cualquier cosa visual."
          },
          service2: {
            title: "Generación de Video IA",
            description: "Videos cortos generados desde estilo, storyboard o descripción. Se ven pulidos, se sienten intencionales y se mueven como algo dirigido — no aleatorio."
          },
          service3: {
            title: "Diseño de Marca & Logo",
            description: "Identidades de marca modernas y minimalistas formadas a través de una mezcla de principios de diseño clásico y exploración IA. Construimos logos, dirección tipográfica, sistemas de color y kits de marca completos."
          },
          service4: {
            title: "Diseño Web",
            description: "Sitios limpios, nítidos y listos para el futuro. Diseñamos todo — layouts hero, estructura, tono de copy, elementos UI — y te ayudamos a lanzar rápido."
          },
          service5: {
            title: "Dirección Creativa",
            description: "Ya sea producción IA o tradicional, guiamos el look, ambiente, ritmo e historia general. Nos aseguramos de que tus visuales se sientan cohesivos en lugar de \"generados.\""
          },
          service6: {
            title: "Herramientas & Sistemas de Producción",
            description: "Construimos flujos de trabajo personalizados que mantienen tu contenido IA consistente — estilo, personajes, prompts y outputs de marca — para que todo se vea unificado en todas las plataformas."
          },
          service7: {
            title: "Agentes Chat (Equipos IA Personalizados)",
            description: "Construimos y entrenamos tus propios agentes IA — un equipo construido alrededor de tu marca, voz, tareas y objetivos. Desde asistentes simples hasta pipelines creativos completos, los estructuramos como un estudio real."
          },
          service8: {
            title: "Filmación & Edición Tradicional",
            description: "Todavía parte de quiénes somos. Filmamos, iluminamos, editamos, colorizamos, cortamos y producimos tradicionalmente cuando se necesita. Esta es la base que mantiene el trabajo IA arraigado e intencional."
          },
          service9: {
            title: "Consultoría de Diseño & Visual",
            description: "Si ya usas IA, te ayudamos a limpiarlo: mejores prompts, mejor control de estilo, personajes estables, outputs consistentes, decisiones creativas más claras."
          },
          service10: {
            title: "Proyectos Creativos Personalizados",
            description: "Cualquier cosa que mezcle cine, diseño e IA — desde conceptos salvajes hasta visuales de marca pulidos. Si es visual o impulsado por historia, podemos ayudar a construirlo."
          },
          customizeButton: "Personaliza Con Nosotros"
        },
        cta: {
          title: "Hagamos Algo",
          description: "Si tienes una idea, veamos en qué puede convertirse — ya sea realista, cinematográfico, bizarro o algo intermedio.",
          button: "Comenzar un Proyecto"
        }
      },
      clarity: {
        title: "Inteligencia clara y útil.",
        body: "Conectamos estrategia, interfaces y contenido para que tu IA se sienta simple, no abrumadora.",
        pillars: {
          strategy: "Estrategia que parte de tus problemas reales.",
          interface: "Interfaces que las personas realmente disfrutan usar.",
          intelligence: "Inteligencia que se explica y genera confianza."
        }
      },
      worlds: {
        title: "Mundos cinematográficos para tu marca.",
        body: "Diseñamos historias visuales que se sienten como cine, construidas con IA."
      },
      systems: {
        title: "Sistemas que crecen contigo.",
        body: "Desde prompts hasta flujos completos, creamos sistemas repetibles que tu equipo puede usar todos los días."
      },
      lab: {
        title: "El Laboratorio Alabama.",
        body: "Un espacio seguro para prototipar nuevas visuales, historias y herramientas antes de invertir."
      },
      flow: {
        title: "Cómo funciona",
        step1Title: "Comparte tu idea",
        step1Body: "Envíanos tus notas, referencias o información de marca. Simple está bien.",
        step2Title: "Construimos la primera versión",
        step2Body: "Generamos tus visuales usando nuestras herramientas internas y dirección creativa.",
        step3Title: "Solicitas ajustes menores",
        step3Body: "Una revisión ligera está incluida en cada Quick Pick.",
        step4Title: "Entrega final",
        step4Body: "Recibes tus assets terminados, listos para usar en cualquier lugar."
      },
      testimonials: {
        title: "Lo que dicen nuestros clientes.",
        empty: "Testimonios muy pronto."
      },
      cta: {
        title: "Listos cuando tú estés.",
        desc: "Cuéntanos lo que quieres construir y diseñamos la IA a su alrededor.",
        button: "Hablar con el estudio"
      }
    },
    services: {
      hero: {
        title: "Lo que construimos.",
        subtitle: "Sistemas de IA, interfaces y narrativas creadas con precisión."
      },
      ai: {
        title: "Consultoría de IA",
        subtitle: "Sistemas inteligentes que trabajan con tu equipo"
      },
      content: {
        title: "Producción de Contenido",
        subtitle: "Historias que conectan y convierten"
      },
      items: {
        ai: {
          title: "Sistemas de IA",
          body: "Flujos inteligentes, agentes y automatizaciones que apoyan a tus equipos."
        },
        interfaces: {
          title: "Diseño de interfaces",
          body: "Interfaces calmas, cinematográficas y claras que se sienten humanas."
        },
        content: {
          title: "Contenido y narrativa",
          body: "Voz de marca, narrativas y flujos editoriales generativos alineados con tu mensaje."
        },
        engineering: {
          title: "Ingeniería creativa",
          body: "Prototipos, experimentos y sistemas en tiempo real que llevan tus ideas más lejos."
        },
        automation: {
          title: "Automatización de Procesos",
          description: "Optimiza operaciones y libera tiempo de tu equipo.",
          features: [
            "Análisis de flujos de trabajo actuales",
            "Diseño de soluciones de automatización personalizadas",
            "Implementación e integración con sistemas existentes"
          ]
        },
        chatbots: {
          title: "Chatbots y Agentes IA",
          description: "Atiende a tus clientes 24/7 con agentes inteligentes.",
          features: [
            "Chatbots conversacionales avanzados",
            "Integración con WhatsApp, web y redes sociales",
            "Aprendizaje continuo y mejora automática"
          ]
        },
        dataAnalysis: {
          title: "Análisis de Datos",
          description: "Transforma datos en insights accionables.",
          features: [
            "Dashboards interactivos y reportes automatizados",
            "Análisis predictivo y forecasting",
            "Recomendaciones basadas en IA"
          ]
        },
        aiImplementation: {
          title: "Implementación de IA",
          description: "Integra modelos de IA en tus productos.",
          features: [
            "Selección de modelos y arquitecturas adecuadas",
            "Desarrollo e integración de APIs",
            "Mantenimiento y optimización continua"
          ]
        },
        strategicConsulting: {
          title: "Consultoría Estratégica",
          description: "Definimos tu roadmap de transformación digital.",
          features: [
            "Auditoría de oportunidades de IA",
            "Diseño de estrategia de implementación",
            "Capacitación y change management"
          ]
        },
        scriptsCopy: {
          title: "Guiones y Copy",
          description: "Textos que convierten y resuenan con tu audiencia.",
          features: [
            "Guiones para videos y podcasts",
            "Copy para landing pages y ads",
            "Contenido optimizado para SEO"
          ]
        },
        socialContent: {
          title: "Contenido para Redes",
          description: "Mantén tu presencia digital siempre activa.",
          features: [
            "Calendarios de contenido estratégicos",
            "Posts, historias y contenido multimedia",
            "Gestión de comunidad y engagement"
          ]
        },
        videoProduction: {
          title: "Producción de Video",
          description: "Videos que capturan atención y generan acción.",
          features: [
            "Videos corporativos y explicativos",
            "Contenido para redes sociales",
            "Edición y post-producción profesional"
          ]
        },
        newsletters: {
          title: "Newsletters",
          description: "Mantén a tu audiencia informada y comprometida.",
          features: [
            "Diseño de estrategia de email marketing",
            "Redacción de contenido persuasivo",
            "Análisis de métricas y optimización"
          ]
        },
        storytelling: {
          title: "Storytelling Estratégico",
          description: "Construye narrativas que definen tu marca.",
          features: [
            "Desarrollo de brand story",
            "Mensajería y posicionamiento",
            "Contenido long-form y thought leadership"
          ]
        },
        aiAudiovisual: {
          title: "Producción Audiovisual con IA",
          description: "Videos de última generación potenciados con inteligencia artificial.",
          features: [
            "Generación de videos con IA",
            "Edición automatizada y efectos",
            "Avatares digitales y voiceovers con IA"
          ]
        },
        upcoming: {
          title: "Próximos Lanzamientos",
          description: "Innovaciones y servicios en desarrollo.",
          features: [
            "Agentes de IA personalizados",
            "Plataformas de automatización completas",
            "Soluciones de IA verticales por industria"
          ]
        }
      },
      cta: {
        title: "Construyamos algo juntos",
        desc: "Cuéntanos tus necesidades y diseñaremos una solución personalizada.",
        button1: "Comenzar",
        button2: "Ver nuestro trabajo"
      },
      filter: {
        all: "Todos",
        ia: "IA",
        content: "Contenido"
      },
      quickpick: {
        title: "Quick Pick Studio",
        subtitle: "Contenido de IA rápido y divertido: elige un paquete, envía tu brief y recibe piezas listas para publicar.",
        cta: "Pedir cotización Quick Pick",
        video: {
          p15: {
            title: "Quick Pick de 15 segundos",
            description: "Perfecto para hooks, teasers, stories y anuncios cortos.",
            price: "$600 MXN"
          },
          p30: {
            title: "Quick Pick de 30 segundos",
            description: "Perfecto para destacar productos, explicaciones simples y anuncios pagados.",
            price: "$900 MXN"
          },
          p45: {
            title: "Quick Pick de 45 segundos",
            description: "Perfecto para explicaciones un poco más profundas y servicios.",
            price: "$1,200 MXN"
          },
          p60: {
            title: "Quick Pick de 60 segundos",
            description: "Perfecto para intros de marca y recorridos completos de producto.",
            price: "$1,500 MXN"
          }
        },
        image: {
          p1: {
            title: "1 Imagen — Flash Hero",
            description: "Perfecto para una imagen clave, hero banner o portada.",
            price: "Desde $50 MXN"
          },
          p4: {
            title: "4 Imágenes — Mini Set",
            description: "Perfecto para campañas cortas, carruseles y sets pequeños de producto.",
            price: "Desde $100 MXN"
          },
          p6: {
            title: "6 Imágenes — Social Pack",
            description: "Perfecto para una semana de posts o lanzamientos sencillos.",
            price: "Desde $180 MXN"
          },
          p10: {
            title: "10 Imágenes — Campaign Pack",
            description: "Perfecto para lanzamientos de producto, campañas multicanal y librerías de contenido.",
            price: "Desde $250 MXN"
          }
        },
        form: {
          name: "Nombre",
          email: "Correo",
          phone: "WhatsApp / teléfono",
          type: "¿Qué necesitas?",
          typeVideo: "Paquete de video",
          typeImage: "Paquete de imágenes",
          typeBoth: "Video e imágenes",
          message: "Describe tu idea, marca y cualquier enlace o referencia",
          submit: "Enviar brief Quick Pick",
          success: "Gracias. Te contactaremos por correo y texto con una cotización.",
          error: "Algo salió mal. Inténtalo de nuevo."
        },
        cart: {
          addToQuote: "Agregar a cotización",
          removeFromQuote: "Eliminar",
          viewQuote: "Ver cotización",
          itemsLabel: "Paquetes seleccionados",
          totalLabel: "Total estimado",
          continueBtn: "Continuar al brief",
          empty: "No hay paquetes seleccionados aún",
          title: "Tu Cotización",
          removeItem: "Eliminar",
          note: "+ IVA. Esto es una solicitud de cotización, no una compra.",
          nameLabel: "Nombre (opcional)",
          emailLabel: "Correo electrónico",
          checkoutCta: "Solicitar Cotización",
          confirmTitle: "¡Solicitud enviada!",
          confirmBody: "Te contactaremos pronto por correo con tu cotización personalizada.",
          errorTitle: "Error",
          errorBody: "Algo salió mal. Inténtalo de nuevo."
        }
      }
    },
    work: {
      hero: {
        title: "Trabajos seleccionados.",
        subtitle: "Sistemas, historias e interfaces creadas para equipos ambiciosos."
      },
      intro: {
        title: "Un estudio moldeado por la experimentación.",
        body: "Cada proyecto es una colaboración, un descubrimiento, un nuevo mundo."
      },
      cases: {
        spectrum_os: {
          title: "Spectrum OS — Sistema de Interfaces de IA",
          summary: "Un entorno sereno para gestionar múltiples agentes de IA, diseñado como una interfaz de vidrio cinematográfica.",
          tags: ["Interfaces de IA", "Multi-agente", "Diseño de producto"]
        },
        lucent: {
          title: "Lucent — Motor Inteligente de Contenido",
          summary: "Un sistema generativo de contenido que mantiene la voz de marca consistente en campañas y canales.",
          tags: ["Automatización de contenido", "Voz de marca", "LLM"]
        },
        zero_point: {
          title: "ZeroPoint — Suite de Orquestación de Agentes",
          summary: "Una capa de orquestación que coordina herramientas, prompts y agentes en una única pila controlable.",
          tags: ["Agentes", "Orquestación", "Sistemas"]
        }
      },
      experimental: {
        title: "Estudios experimentales de IA",
        body: "Conceptos, prototipos y experimentos visuales donde exploramos movimiento, atmósfera e imagen generada por IA."
      }
    },
    about: {
      hero: {
        title: "Construido con producción real. Expandido con IA.",
        subtitle: "Construido sobre artesanía tradicional. Mejorado con herramientas de IA.",
        scrollCta: "Nuestro trabajo en crecimiento"
      },
      story: {
        title: "Quiénes somos.",
        p1: "Venimos de producto, cine y branding.",
        p2: "Hablamos negocio, diseño y tecnología al mismo tiempo.",
        p3: "Nuestro trabajo es sereno, intencional y hecho para durar."
      },
      media: {
        portrait: {
          title: "Un estudio, no una fábrica.",
          desc: "Cada proyecto se trata como una colaboración, no como un ticket."
        }
      },
      approach: {
        title: "Nuestro enfoque",
        ai: {
          title: "Estrategia de IA",
          desc: "Te ayudamos a entender qué puede hacer realmente la IA por tu negocio, no solo lo que está de moda."
        },
        creative: {
          title: "Pensamiento creativo",
          desc: "Cada solución se adapta a la voz, audiencia y objetivos de tu marca."
        }
      },
      team: {
        title: "El equipo"
      },
      values: {
        title: "Nuestros valores"
      },
      cta: {
        title: "¿Listos para trabajar juntos?",
        desc: "Hablemos de tu proyecto.",
        button: "Ponte en contacto"
      }
    },
    contact: {
      hero: {
        title: "Cuéntanos lo que quieres construir.",
        subtitle: "Un mensaje breve es suficiente. Te respondemos con ideas, preguntas y un siguiente paso claro."
      },
      form: {
        name: "Nombre",
        email: "Correo electrónico",
        company: "Empresa",
        service: "¿Qué te interesa?",
        budget: "Rango de presupuesto",
        message: "¿Qué quieres crear?",
        submit: "Enviar mensaje",
        success: "Gracias. Nos pondremos en contacto pronto.",
        error: "Algo salió mal. Inténtalo de nuevo.",
        required: "*",
        placeholder: {
          name: "Tu nombre",
          email: "tu@email.com",
          company: "Tu empresa",
          service: "Selecciona un servicio",
          budget: "Selecciona un rango",
          message: "Cuéntanos sobre tu proyecto..."
        }
      },
      info: {
        title: "Contacto",
        email: "Correo",
        whatsapp: "WhatsApp",
        location: "Ubicación",
        chat: "Chatear por WhatsApp",
        "location.address": "Alabama 29, CDMX"
      },
      expect: {
        title: "Qué esperar",
        "1": "Te respondemos en 24 horas",
        "2": "Una llamada corta para entender tus necesidades",
        "3": "Una propuesta clara con cronograma y precios",
        "4": "Sin presión, sin discurso de ventas"
      },
      faq: {
        title: "Preguntas frecuentes",
        subtitle: "Respuestas rápidas sobre trabajar con nosotros",
        more: "¿Tienes más preguntas?",
        button: "Preguntar por WhatsApp",
        q1: {
          question: "¿Cuánto tiempo toma un proyecto típico?",
          answer: "Depende del alcance, pero la mayoría de nuestros proyectos de consultoría de IA se completan en 4-8 semanas, mientras que los proyectos de contenido pueden variar de 2-6 semanas. Trabajamos con sprints ágiles para entregar valor incremental."
        },
        q2: {
          question: "¿Trabajan con empresas de cualquier tamaño?",
          answer: "Sí, trabajamos tanto con startups en etapa temprana como con empresas establecidas. Adaptamos nuestros servicios y propuestas al tamaño y necesidades específicas de cada cliente."
        },
        q3: {
          question: "¿Ofrecen soporte post-implementación?",
          answer: "Absolutamente. Todos nuestros proyectos incluyen un período de soporte inicial, y ofrecemos planes de mantenimiento continuo para asegurar que tus soluciones de IA y contenido sigan funcionando óptimamente."
        },
        q4: {
          question: "¿Necesito conocimientos técnicos para trabajar con ustedes?",
          answer: "Para nada. Nos encargamos de toda la parte técnica y te explicamos todo en términos de negocio. Nuestro objetivo es que entiendas el valor y los resultados, no los tecnicismos."
        },
        q5: {
          question: "¿Cómo miden el éxito de un proyecto?",
          answer: "Definimos KPIs claros desde el inicio: ROI, tiempo ahorrado, aumento en conversión, engagement, etc. Cada proyecto incluye reportes con métricas concretas para medir el impacto real en tu negocio."
        },
        q6: {
          question: "¿Puedo contratar solo consultoría de IA o solo contenido?",
          answer: "Por supuesto. Aunque muchos clientes aprovechan ambos servicios para una estrategia integral, puedes contratar solo lo que necesites. Te ayudaremos a identificar qué es más prioritario para tu situación."
        },
        q7: {
          question: "¿Trabajan con clientes internacionales?",
          answer: "Sí, trabajamos con clientes en toda Latinoamérica, Estados Unidos y Europa. Nuestro equipo está acostumbrado a colaborar de forma remota y en diferentes zonas horarias."
        },
        q8: {
          question: "¿Qué diferencia a Alabama Estudio de otras agencias?",
          answer: "Somos únicos en combinar expertise técnico en IA con creatividad de alto nivel. No somos solo desarrolladores ni solo creativos: entendemos profundamente ambos mundos y cómo hacerlos trabajar juntos para generar resultados reales."
        },
        q9: {
          question: "¿Cómo empezamos a trabajar juntos?",
          answer: "Es muy simple: agenda una llamada inicial gratuita donde conoceremos tu negocio y desafíos. Luego te enviamos una propuesta personalizada. Si te convence, ¡arrancamos! Toda la comunicación es clara y sin letra chica."
        }
      },
      services: {
        selectedLabel: "Servicios Seleccionados",
        serviceOptions: {
          ia: "Consultoría de IA",
          content: "Producción de Contenido",
          both: "Ambos servicios",
          other: "Otro"
        }
      }
    },
    app: {
      loading: {
        label: "Cargando la experiencia…",
        alt: "Ilustración de carga de ALABAMA Ai"
      }
    },
    footer: {
      companyName: "Alabama Estudio",
      tagline: "Make. Play. Repeat.",
      quicklinks: "Enlaces rápidos",
      services: "Servicios",
      rights: "Todos los derechos reservados.",
      ai: "Consultoría de IA",
      content: "Estudio de Contenido",
      hello: "Hola"
    },
    quickpick: {
      hero: {
        title: "Elige una dirección",
        subtitle: "Un punto de entrada — no una decisión."
      },
      arrival: {
        title: "Elige una dirección",
        subtitle: "Un punto de entrada, no una decisión.",
        enter: "Entrar"
      },
      directions: {
        social: {
          title: "Dirección Social",
          philosophy: "Creación de imágenes inmediata y estética.\nPensada para presencia, ritmo y cultura — visuales rápidas que se sienten actuales sin ser desechables."
        },
        cinematic: {
          title: "Creador Cinematográfico",
          philosophy: "Video guiado por historia, movimiento e intención.\nLa iluminación, el ritmo y la narrativa van primero — pensado para sentirse cuidado, no automático."
        },
        suite: {
          title: "Suite Cinematográfica Completa",
          philosophy: "Construcción de mundos a través de imagen y movimiento.\nUn sistema visual completo donde escala, tono y continuidad trabajan juntos."
        }
      },
      packages: {
        social: {
          title: "Dirección Social",
          oneliner: "Un pase rápido de dirección visual para que tu marca se vea intencional.",
          included: [
            "Un set compacto de imágenes listas para social y ads",
            "Un look claro: color, contraste, textura",
            "Recortes para los formatos que sí usas",
            "Pulido ligero, entrega rápida"
          ],
          ctaHelper: "Ideal si necesitas momentum, no una producción grande."
        },
        cinematic: {
          title: "Creador Cinemático",
          oneliner: "Video corto dirigido como cine: limpio, con ritmo y precisión.",
          included: [
            "Un clip principal con pocos shots, pero fuertes",
            "Ritmo de edición + títulos editoriales",
            "Pulido de sonido (sin efectos baratos)",
            "Versiones cortas para redes si hace falta"
          ],
          ctaHelper: "Para marcas que quieren \"cinemático\" sin inflarlo."
        },
        suite: {
          title: "Suite Cinemática Completa",
          oneliner: "El paquete grande: historia, dirección y un set completo de assets.",
          included: [
            "Concepto + estructura (primero armamos la historia)",
            "Un pase tipo \"director suite\": control de cámara, lente, luz y puesta en escena",
            "Video principal + versiones cortas",
            "Stills coherentes con el look del video",
            "Pulido premium + consistencia total"
          ],
          ctaHelper: "Si quieres el resultado más alto, empieza aquí."
        }
      },
      framing: {
        short: "Formato corto — un momento preciso e intencional",
        extended: "Extendido — espacio para desarrollar",
        long: "Formato largo — arco narrativo completo"
      },
      cta: {
        enter: "Entrar"
      }
    },
    generate: {
      banner: {
        title: "DICIEMBRE:",
        subtitle: "Imágenes GRATIS + Acceso Anticipado a Alabama Ai.",
        expires: "Válido hasta el 31 de Dic.",
        placeholder: "Tu email",
        button: "Obtener Acceso",
        success: "¡Estás dentro!"
      },
      empty: {
        title: "Tus imágenes generadas aparecerán aquí",
        subtitle: "Empieza describiendo lo que quieres crear"
      },
      processing: "Procesando",
      placeholder: "Crea lo que quieras...",
      button: "Generar",
      tooltip: "Por favor escribe un prompt para generar.",
      dragHint: "Arrastra imágenes para editar",
      edit: "Editar",
      download: "Descargar",
      downloadSuccess: "¡Imagen descargada!",
      shareSuccess: "¡Imagen compartida!",
      downloadCancelled: "Descarga cancelada",
      copySuccess: "¡URL de imagen copiada!",
      editHint: "Imagen añadida para editar. Agrega tu prompt de edición.",
      unlimited: "Ilimitado",
      toastCreated: "Imagen creada",
      toastCreatedMultiple: "imágenes creadas",
      toastListSuccess: "¡Estás en la lista! Te notificaremos cuando el acceso anticipado esté listo.",
      toastError: "Algo salió mal. Por favor intenta de nuevo.",
      invalidFile: "Archivo inválido",
      invalidFileDesc: "Por favor sube un archivo de imagen.",
      generationFailed: "Error de generación",
      generationFailedDesc: "Por favor intenta de nuevo.",
      addPrompt: "Agrega un prompt para generar",
      maxProcessing: "Máximo 3 imágenes procesando a la vez. Por favor espera.",
      promptDirector: {
        label: "Prompt Director",
        subtitle: "Dirección, refinamiento y continuación.",
        minimize: "Minimizar",
        close: "Cerrar",
        restore: "Abrir Prompt Director",
        grid: "Cuadrícula",
        size: "Tamaño",
        yourRenders: "Tus renders",
        hint: "Habla con el director creativo."
      },
      mai: {
        director: "MAI — Director",
        minimize: "Minimizar",
        close: "Cerrar",
        backToGallery: "Volver a la galería",
        grid: "Cuadrícula",
        size: "Tamaño",
        yourRenders: "Tus renders",
        continue: "Continuar"
      },
      // Active selection states
      active: "Activo",
      setActive: "Activar",
      selectedForDirector: "Seleccionado para el Director",
      deselected: "Deseleccionado"
    }
  }
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Check for manually saved preference first
    const saved = localStorage.getItem("alabama-language");
    if (saved === "en" || saved === "es") return saved;

    // Auto-detect from browser locale on first visit
    const browserLang = navigator.language || (navigator as any).userLanguage || '';
    const detected: Language = browserLang.startsWith('es') ? 'es' : 'en';
    localStorage.setItem("alabama-language", detected);
    return detected;
  });

  useEffect(() => {
    localStorage.setItem("alabama-language", language);
  }, [language]);

  const t = (key: string): any => {
    const keys = key.split(".");
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k];
      } else {
        return key;
      }
    }
    
    // Return arrays and strings as-is
    if (Array.isArray(value) || typeof value === "string") {
      return value;
    }
    
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
